// ═══════════════════════════════════════════════════════════════════════════════
// Receipt Validation Schemas
// ═══════════════════════════════════════════════════════════════════════════════
import { z } from 'zod';
import { RECEIPT_STATUS, SUPPORTED_LANGUAGES } from '../constants/index.js';
import { dateSchema, uuidSchema, positiveIntSchema, emptyOrNumberSchema, currencyCodeSchema, } from './common.schema.js';
// ── Receipt Item Schema ──
export const receiptItemSchema = z.object({
    item: z.string().min(1, 'Item name is required'),
    qty: z.union([
        z.number(),
        z.string(),
        z.literal(''),
    ]).default(''),
    amount: emptyOrNumberSchema,
});
// ── Receipt Data Schema (from AI) ──
export const receiptDataSchema = z.object({
    company: z.string().default(''),
    shop: z.string().default(''),
    date: z.string().refine((val) => val === '' || /^\d{4}-\d{2}-\d{2}$/.test(val), 'Date must be YYYY-MM-DD or empty').default(''),
    currency: currencyCodeSchema.default(''),
    totalAmount: emptyOrNumberSchema,
    items: z.array(receiptItemSchema).default([]),
    remark: z.string().default(''),
});
// ── Create Receipt Input Schema ──
export const createReceiptInputSchema = z.object({
    receiptDate: dateSchema,
    company: z.string().max(200).default('').transform((val) => val.trim()),
    shop: z.string().max(200).default('').transform((val) => val.trim()),
    currency: z.string().max(3).default('HKD'),
    totalAmount: z.number().min(0, 'Total amount must be non-negative'),
    items: z.array(receiptItemSchema).default([]),
    remark: z.string().max(500).default('').transform((val) => val.trim()),
    aiRawResponse: z.string().default(''),
    originalImageUrl: z.string().url('Invalid image URL'),
});
// ── Update Receipt Input Schema ──
export const updateReceiptInputSchema = z.object({
    receiptDate: dateSchema.optional(),
    company: z.string().max(200).transform((val) => val.trim()).optional(),
    shop: z.string().max(200).transform((val) => val.trim()).optional(),
    currency: z.string().max(3).optional(),
    totalAmount: z.number().min(0).optional(),
    items: z.array(receiptItemSchema).optional(),
    remark: z.string().max(500).transform((val) => val.trim()).optional(),
    version: positiveIntSchema,
});
// ── Review Receipt Input Schema ──
export const reviewReceiptInputSchema = z.object({
    version: positiveIntSchema,
});
// ── Delete Receipt Query (version for optimistic locking) ──
export const receiptDeleteQuerySchema = z.object({
    version: z.coerce.number().int().min(1, 'Version must be at least 1'),
});
// ── Receipt ID Param Schema ──
export const receiptIdParamSchema = z.object({
    id: uuidSchema,
});
/** Optional viewer UI language for receipt detail translations (may differ from saved settings). */
export const receiptDetailQuerySchema = z.object({
    viewerLanguage: z.enum(SUPPORTED_LANGUAGES).optional(),
});
// ── Receipt Query Schema ──
export const receiptQuerySchema = z.object({
    budgetId: uuidSchema.optional(),
    status: z.enum([
        RECEIPT_STATUS.SAVE,
        RECEIPT_STATUS.SUBMITTED,
        RECEIPT_STATUS.REVIEWED,
        'all',
    ]).default('all'),
    startDate: dateSchema.optional(),
    endDate: dateSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
//# sourceMappingURL=receipt.schema.js.map